import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  IconButton, 
  Chip,
  Typography,
  Paper
} from '@mui/material';
import { Close as CloseIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';

interface FilterCondition {
  id: string;
  column: string;
  operator: string;
  value: string;
}

interface TableFilterProps {
  columns: GridColDef[];
  onFiltersChange: (filters: FilterCondition[]) => void;
}

const operators = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
];

const TableFilter: React.FC<TableFilterProps> = ({ columns, onFiltersChange }) => {
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const filterableColumns = columns.filter(col => col.filterable !== false);

  const addFilter = () => {
    const newFilter: FilterCondition = {
      id: Date.now().toString(),
      column: filterableColumns[0]?.field || '',
      operator: 'contains',
      value: '',
    };
    const updatedFilters = [...filters, newFilter];
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const removeFilter = (filterId: string) => {
    const updatedFilters = filters.filter(f => f.id !== filterId);
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const updateFilter = (filterId: string, field: keyof FilterCondition, value: string) => {
    const updatedFilters = filters.map(f => 
      f.id === filterId ? { ...f, [field]: value } : f
    );
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const clearAllFilters = () => {
    setFilters([]);
    onFiltersChange([]);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton 
          onClick={() => setShowFilters(!showFilters)}
          size="small"
          color={showFilters ? 'primary' : 'default'}
        >
          <FilterIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          Filters
        </Typography>
        {filters.length > 0 && (
          <Chip 
            label={`${filters.length} active`} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        )}
        {filters.length > 0 && (
          <Typography 
            variant="body2" 
            color="primary" 
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={clearAllFilters}
          >
            Clear all
          </Typography>
        )}
      </Box>

      {showFilters && (
        <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filters.map((filter) => (
              <Box key={filter.id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Column</InputLabel>
                  <Select
                    value={filter.column}
                    label="Column"
                    onChange={(e) => updateFilter(filter.id, 'column', e.target.value)}
                  >
                    {filterableColumns.map((col) => (
                      <MenuItem key={col.field} value={col.field}>
                        {col.headerName || col.field}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Operator</InputLabel>
                  <Select
                    value={filter.operator}
                    label="Operator"
                    onChange={(e) => updateFilter(filter.id, 'operator', e.target.value)}
                  >
                    {operators.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Value"
                  value={filter.value}
                  onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                  placeholder="Filter value"
                  sx={{ flex: 1 }}
                  disabled={filter.operator === 'isEmpty' || filter.operator === 'isNotEmpty'}
                />

                <IconButton 
                  onClick={() => removeFilter(filter.id)}
                  size="small"
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Typography 
                variant="body2" 
                color="primary" 
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={addFilter}
              >
                + Add filter
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default TableFilter;
